import { InfoIcon, ToastContainer, UIStyle } from "@povio/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { basicLogger, useFlags, withLDProvider } from "launchdarkly-react-client-sdk";
import type { AppProps } from "next/app";
import { useRouter } from "next/router";
import { useReportWebVitals } from "next/web-vitals";
import { NextStepProvider } from "nextstepjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SkeletonTheme } from "react-loading-skeleton";

import { tableData, tableHeadData, tableHeadRow, tableRow } from "@/styles/overrides/table.cva";
import { typography } from "@/styles/overrides/typography.cva";
import "react-loading-skeleton/dist/skeleton.css";

import GoogleAnalytics from "@/components/googleAnalytics/GoogleAnalytics";
import { GlobalLiveshowModal } from "@/components/liveshow/GlobalLiveshowModal";
import { LoyaltyModal } from "@/components/loyalty/LoyaltyModal";
import { DynamicFavicon } from "@/components/shared/DynamicFavicon";
import { ErrorBoundaryTrigger } from "@/components/shared/error/ErrorBoundaryTrigger";
import { DefaultAppHead } from "@/components/shared/head/DefaultAppHead";
import { PageWrapper } from "@/components/shared/layout/PageWrapper";
import { SerwistRegistration } from "@/components/shared/SerwistRegistration";
import AppDisabled from "@/components/signUpIn/AppDisabled";
// import { SubscribeModal } from "@/components/subscription/SubscribeModal";
import NextStepWithDemoContext from "@/components/ui/tours/NextStepWithDemoContext";
import { GlobalWelcomeRewardModal } from "@/components/wallet/GlobalWelcomeRewardModal";
import { AppConfig } from "@/config/app.config";
import { initA11y } from "@/config/inits/a11y";
import { initSentry } from "@/config/inits/sentry";
import { QueryConfig } from "@/config/query.config";
import { AuthClerkProvider } from "@/data/auth/auth-clerk/auth-clerk.provider";
import { JobSalaryProvider } from "@/data/watchlist/JobSalary.context";
import Providers from "@/providers";
import { AppErrorBoundary } from "@/providers/AppErrorBoundary";
import { ChatSocketProvider } from "@/providers/ChatSocketContext";
import { DemoContext } from "@/providers/demo/DemoContext";
import { LoyaltySocketSubscriber } from "@/providers/loyalty/LoyaltySocketSubscriber";
import { NotificationSocketSubscriber } from "@/providers/notifications/NotificationSocketSubscriber";
import { OverlayQueueProvider } from "@/providers/overlay/OverlayQueueProvider";
import { Fonts } from "@/styles/font";
import "@/styles/globals.css";

import { useToast } from "@/hooks/useToast";
import { SseProvider } from "@/providers/SseContext";
import { button, buttonContent, buttonIconSize, buttonTypography } from "@/styles/overrides/button.cva";
import { checkbox, checkboxTypography } from "@/styles/overrides/checkbox.cva";
import { inputBase, inputSide, inputSize } from "@/styles/overrides/input.cva";
import { modalContent, modalMain, modalOverlay } from "@/styles/overrides/modal.cva";
import { pillButton } from "@/styles/overrides/pillButton.cva";
import { iconCva, toast } from "@/styles/overrides/toast.cva";
import { toggle, toggleTypography } from "@/styles/overrides/toggle.cva";
import { logger } from "@/util/logger";

initSentry();
initA11y();

export function App({ Component, pageProps }: AppProps) {
  const [activeToast, setActiveToast] = useState<React.ReactNode | null>(null);
  const { i18n } = useTranslation();
  const [queryClient] = useState(() => new QueryClient(QueryConfig));
  const router = useRouter();
  const { neutralToast } = useToast();
  const { cryptoWallet } = useFlags();
  useEffect(() => {
    i18n.on("languageChanged", (lng) => {
      if (typeof document !== "undefined") {
        document.documentElement.setAttribute("lang", lng);
      }
    });
  }, [i18n]);

  useReportWebVitals((metric) => {
    logger.info("Web Vitals", metric);
  });

  const checkVersion = useCallback(async () => {
    try {
      const response = await fetch("/api/version.json", {
        cache: "no-store",
        headers: { "Content-Type": "application/json" },
      });
      const data = await response.json();
      const serverVersion = data.version;
      logger.info("Server version: ", serverVersion);
      const clientVersion = AppConfig.version;
      logger.info("Client version: ", clientVersion);

      if (serverVersion !== clientVersion && clientVersion !== undefined) {
        logger.info("Version mismatch: ", serverVersion, clientVersion);
        if (activeToast) {
          return;
        }

        const id = neutralToast(
          {
            text: "New version of LossDog is available. Refresh the page or click this message to get the latest features and improvements.",
            position: "bottom-right",
            icon: InfoIcon,
            variant: "outlined",
          },
          {
            onClose: () => {
              setActiveToast(null);
              router.reload();
            },
          },
        );

        setActiveToast(id);
      }
    } catch (error) {
      logger.error("Error checking version", error);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [neutralToast, router]);

  useEffect(() => {
    checkVersion();
  }, [checkVersion]);

  if (AppConfig.isAppDisabled) {
    return <AppDisabled />;
  }

  return (
    <>
      <GoogleAnalytics />
      <SerwistRegistration />
      <AppErrorBoundary>
        <Providers
          providers={[
            { provider: NextStepProvider },
            { provider: QueryClientProvider, props: { client: queryClient } },
            { provider: AuthClerkProvider },
            { provider: SseProvider },
            { provider: DemoContext.Provider },
            { provider: JobSalaryProvider },
            { provider: ChatSocketProvider },
            { provider: OverlayQueueProvider },
            {
              provider: UIStyle.Provider,
              props: {
                config: {
                  typography: { cva: typography },
                  button: {
                    cva: button,
                    contentCva: buttonContent,
                    iconSize: buttonIconSize,
                    typography: buttonTypography,
                  },
                  pillButton: { cva: pillButton },
                  input: { baseCva: inputBase, sizeCva: inputSize, sideCva: inputSide },
                  modal: { contentCva: modalContent, overlayCva: modalOverlay, mainCva: modalMain },
                  toggle: { cva: toggle, typography: toggleTypography },
                  checkbox: { cva: checkbox, typography: checkboxTypography },
                  table: { headDataCva: tableHeadData, headRowCva: tableHeadRow, rowCva: tableRow, dataCva: tableData },
                  toast: { cva: toast },
                  status: { iconCva },
                },
              },
            },
            { provider: SkeletonTheme, props: { baseColor: "#232323", highlightColor: "#656565" } },
          ]}
        >
          <NextStepWithDemoContext>
            <ErrorBoundaryTrigger />
            <Fonts />
            <DefaultAppHead />
            <DynamicFavicon />
            <PageWrapper>
              <Component {...pageProps} />
              <NotificationSocketSubscriber />
              <LoyaltySocketSubscriber />
              <GlobalLiveshowModal />
              {cryptoWallet && <GlobalWelcomeRewardModal />}
              <LoyaltyModal />
              {/* Subscription modal - commented out */}
              {/* <SubscribeModal /> */}
              <ToastContainer />
            </PageWrapper>
            {!AppConfig.isProduction && <ReactQueryDevtools initialIsOpen={false} />}
          </NextStepWithDemoContext>
        </Providers>
      </AppErrorBoundary>
    </>
  );
}

export default withLDProvider({
  clientSideID: AppConfig.launchDarkly.clientSideId,
  options: {
    bootstrap: "localStorage",
    logger: basicLogger({ level: "error" }),
  },
  // @ts-ignore: LaunchDarkly's types don't really support Next.js
})(App);
